import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, Calendar, Clock, Hotel, CheckCircle, Circle, UserCheck, User, FileText, Users, X, Eye, Download, Send, Archive, ArrowRight } from 'lucide-react';
import { apiService, contactsAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { EVENTS_LIST_PATH, isEventPast, eventRegistrationPath, canRegister } from '../config/events';
import useMyEvents from '../hooks/useMyEvents';
import NavigationBar from './NavigationBar';
import EventNavigation from './EventNavigation';
import LoadingSpinner from './LoadingSpinner';
import DonorCircleTasks from './DonorCircleTasks';
import './EventHome.css';
import '../pages/EventEntityCompanies.css';

// Labels for the profile review status pill shown on approval-gated entity tasks
const PROFILE_STATUS_LABEL = {
    draft: 'Draft',
    submitted: 'Submitted',
    in_review: 'In review',
    changes_requested: 'Changes requested',
    live: 'Live',
    withdrawn: 'Withdrawn',
};

const EventHome = () => {
    const { eventNameId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [hotelBookingLoading, setHotelBookingLoading] = useState(false);
    const [hotelBookingError, setHotelBookingError] = useState('');
    const [hotelNotNeededLoading, setHotelNotNeededLoading] = useState(false);
    const [userEventCompanyContact, setUserEventCompanyContact] = useState(null);
    const [showHotelPanel, setShowHotelPanel] = useState(false);
    const [contactProfile, setContactProfile] = useState(null);
    const [companyEntitiesWithForms, setCompanyEntitiesWithForms] = useState([]);
    const [reviewSubmitting, setReviewSubmitting] = useState({});
    const [reviewMessage, setReviewMessage] = useState(null); // { type: 'success'|'error', text }

    // Colleagues state
    const [colleagues, setColleagues] = useState([]);
    const [showColleaguesDialog, setShowColleaguesDialog] = useState(false);
    const [colleaguesLoading, setColleaguesLoading] = useState(false);
    const [colleagueBookingLoading, setColleagueBookingLoading] = useState({});
    const [hasConfirmedColleagues, setHasConfirmedColleagues] = useState(false);

    // Dynamic tasks based on configuration
    const [tasks, setTasks] = useState([]);
    const [allTasksComplete, setAllTasksComplete] = useState(false);
    // Donor Circle task status per Donor event_company_id: { nominationDone, commitmentDone }
    const [donorTaskStatus, setDonorTaskStatus] = useState({});

    // Profile preview drawer state
    const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
    const [profileDrawerData, setProfileDrawerData] = useState(null);
    const [profileDrawerLoading, setProfileDrawerLoading] = useState(false);

    // The signed-in contact's registration status for this event (from the events list)
    const { events: myEvents } = useMyEvents();
    const myEvent = myEvents.find((e) => e.EventNameId === eventNameId) || null;
    // null while the list is loading / event unknown -> fall back to the legacy "approved" task
    const registrationStatus = myEvent ? (myEvent.registration?.status || 'none') : null;
    const showRegisterCta = !!myEvent && canRegister(myEvent);
    // Incremented on every event change so responses from a previous event are ignored
    const fetchSeqRef = useRef(0);
    const registerPath = `${eventRegistrationPath(eventNameId)}${user?.email ? `?email=${encodeURIComponent(user.email)}` : ''}`;

    useEffect(() => {
        if (eventNameId) {
            localStorage.setItem('last_event', eventNameId);
            // Reset per-event state so switching events never shows the previous event's data
            setUserEventCompanyContact(null);
            setShowHotelPanel(false);
            setCompanyEntitiesWithForms([]);
            setDonorTaskStatus({});
            setReviewMessage(null);
            setHotelBookingError('');
            setColleagues([]);
            setHasConfirmedColleagues(false);
            fetchEventData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventNameId]);

    // Fetch colleagues count when hotel panel becomes visible
    useEffect(() => {
        const checkForColleagues = async () => {
            if (showHotelPanel && event?.EventId) {
                try {
                    const response = await apiService.getEventColleagues(event.EventId);
                    if (response.data.success && response.data.colleagues?.length > 0) {
                        setHasConfirmedColleagues(true);
                        setColleagues(response.data.colleagues);
                    } else {
                        setHasConfirmedColleagues(false);
                    }
                } catch (err) {
                    console.error('Error checking for colleagues:', err);
                    setHasConfirmedColleagues(false);
                }
            }
        };
        checkForColleagues();
    }, [showHotelPanel, event?.EventId]);

    // Check if user has Non-profit entity
    const isNonProfit = companyEntitiesWithForms.some(entity =>
        entity.entity_name?.toLowerCase().includes('non-profit') ||
        entity.entity_name?.toLowerCase().includes('nonprofit')
    );

    // Update tasks when panel visibility changes or profile loads
    useEffect(() => {
        const registrationTask = (() => {
            const base = { id: 1, icon: UserCheck, actionText: null };
            switch (registrationStatus) {
                case null:
                case 'confirmed':
                    return { ...base, title: 'Registration Approved', description: 'Your registration has been approved', status: 'completed' };
                case 'in_review':
                    return { ...base, title: 'Registration under review', description: 'We’re reviewing your registration and will email you once it’s confirmed.', status: 'pending' };
                case 'pending_payment':
                case 'needs_invoice':
                    return { ...base, title: 'Payment pending', description: 'Your registration will be confirmed once payment is received.', status: 'pending' };
                case 'registering':
                    return { ...base, title: 'Finish your registration', description: 'You started registering but didn’t finish.', status: 'pending', actionText: 'Continue registration' };
                case 'declined':
                    return { ...base, title: 'Registration declined', description: 'Contact service@funds4charity.org if you have questions.', status: 'pending' };
                case 'cancelled':
                case 'inactive':
                    return { ...base, title: 'Registration cancelled', description: 'You can register again if you’d like to attend.', status: 'pending', actionText: 'Register' };
                case 'none':
                default:
                    return { ...base, title: 'Register for this event', description: 'You’re not registered for this event yet.', status: 'pending', actionText: 'Register' };
            }
        })();
        // Archived events (and anything else canRegister rejects) never offer a registration action
        if (!showRegisterCta) registrationTask.actionText = null;

        const newTasks = [
            registrationTask,
            {
                id: 2,
                title: "Update Personal Profile",
                description: "Complete your profile information",
                status: contactProfile?.is_onboarded ? "completed" : "pending",
                icon: User,
                actionText: contactProfile?.is_onboarded ? "View Profile" : "Update Profile"
            }
        ];

        // Add hotel panel if it should be shown
        if (showHotelPanel) {
            // Determine hotel task status based on passKeyEventStatusId and hotelNotNeeded
            const passKeyStatusId = userEventCompanyContact?.passKeyEventStatusId;
            const hotelNotNeeded = userEventCompanyContact?.hotelNotNeeded;

            let hotelTask = {
                id: 3,
                title: "Book a Hotel",
                description: "Reserve your accommodation for the event",
                status: "pending",
                icon: Hotel,
                actionText: "Book Hotel",
                disabled: false,
                hotelNotNeeded: hotelNotNeeded
            };

            if (hotelNotNeeded) {
                // User marked hotel as not needed - show as completed
                hotelTask = {
                    ...hotelTask,
                    title: "Hotel Not Required",
                    description: "You’ve indicated that you do not need hotel accommodations for this event.",
                    status: "completed",
                    actionText: "Book Hotel",
                    hotelNotNeeded: true
                };
            } else if (passKeyStatusId === 0 || passKeyStatusId === 2) {
                // Reservation exists or confirmed - show "Hotel Booked" as completed
                hotelTask = {
                    ...hotelTask,
                    title: "Hotel Booked",
                    description: "Your hotel reservation is confirmed",
                    status: "completed",
                    actionText: "Manage Reservation"
                };
            } else if (passKeyStatusId === 1) {
                // Reservation cancelled - show as disabled
                hotelTask = {
                    ...hotelTask,
                    title: "Reservation Cancelled",
                    description: "Your hotel reservation has been cancelled",
                    status: "pending",
                    actionText: "Reservation Cancelled",
                    disabled: true
                };
            } else {
                // No reservation (PasskeyStatusId is not 0 or 2) - hotels are sold out
                hotelTask = {
                    ...hotelTask,
                    title: "Book a Hotel",
                    description: "Reserve your accommodation for the event",
                    status: "pending",
                    actionText: "Sold Out",
                    disabled: true
                };
            }

            newTasks.push(hotelTask);
        }

        // Add company entity form tasks (starting from id 100 to avoid conflicts)
        companyEntitiesWithForms.forEach((entity, index) => {
            const review = describeProfileReview(entity);
            newTasks.push({
                id: 100 + index,
                title: review.title,
                description: review.description,
                status: review.status,
                icon: FileText,
                actionText: review.actionText,
                companyEntityId: entity.company_entity_id,
                entityId: entity.entity_id,
                entityName: entity.entity_name,
                formName: entity.form_name,
                isOnboarded: entity.is_onboarded,
                eventCompanyId: entity.event_company_id,
                requiresApproval: !!entity.requires_profile_approval,
                profileStatus: entity.profile_status,
                canSubmitForReview: review.canSubmit
            });
        });

        setTasks(newTasks);

        // Check if all tasks (excluding hotel) are complete, including the Donor Circle tasks for Donor orgs
        const nonHotelTasks = newTasks.filter(task => task.id !== 3);
        const donorEntities = (companyEntitiesWithForms || []).filter((e) => e.entity_name === 'Donor' && e.event_company_id);
        const donorTasksComplete = donorEntities.every((e) => {
            const st = donorTaskStatus[e.event_company_id];
            return !!(st && st.nominationDone && st.commitmentDone);
        });
        const allComplete = nonHotelTasks.length > 0 && nonHotelTasks.every(task => task.status === 'completed') && donorTasksComplete;
        setAllTasksComplete(allComplete);
    }, [showHotelPanel, contactProfile, companyEntitiesWithForms, userEventCompanyContact, donorTaskStatus, registrationStatus, showRegisterCta]);

    const handleDonorTaskStatus = useCallback((eventCompanyId, status) => {
        setDonorTaskStatus((prev) => {
            const cur = prev[eventCompanyId];
            if (cur && cur.nominationDone === status.nominationDone && cur.commitmentDone === status.commitmentDone) return prev;
            return { ...prev, [eventCompanyId]: status };
        });
    }, []);

    const fetchEventData = async () => {
        const seq = ++fetchSeqRef.current;
        const stale = () => seq !== fetchSeqRef.current;
        try {
            setLoading(true);
            setError('');
            const response = await apiService.getEventByNameId(eventNameId);
            if (stale()) return;
            setEvent(response.data);

            // Fetch user's profile to check onboarding status
            if (user?.contact_id) {
                try {
                    const profileResponse = await contactsAPI.getMyProfile();
                    if (stale()) return;
                    setContactProfile(profileResponse.data);
                } catch (profileErr) {
                    console.warn('Could not fetch user profile:', profileErr);
                }

                // Fetch company entities with forms for the user
                try {
                    const entitiesResponse = await apiService.getMyCompanyEntitiesWithForms(eventNameId);
                    if (stale()) return;
                    setCompanyEntitiesWithForms(entitiesResponse.data);
                } catch (entitiesErr) {
                    console.warn('Could not fetch company entities with forms:', entitiesErr);
                }
            }

            // Fetch user's EventCompanyContact information for this event
            if (response.data && response.data.EventId) {
                try {
                    const contactResponse = await apiService.getUserEventCompanyContact(response.data.EventId);
                    if (stale()) return;
                    setUserEventCompanyContact(contactResponse.data);

                    // Set panel visibility based on event entity configuration
                    if (contactResponse.data) {
                        setShowHotelPanel(contactResponse.data.passKeyEventCode && contactResponse.data.passKeyEventCode.trim() !== '');
                    }
                } catch (contactErr) {
                    console.warn('Could not fetch user event company contact:', contactErr);
                    // This is not critical, so we don't set an error
                }
            }
        } catch (err) {
            if (stale()) return;
            console.error('Error fetching event:', err);
            setError('Failed to load event details');
        } finally {
            if (!stale()) setLoading(false);
        }
    };

    // Get the event timezone for consistent display across all attendees
    const getEventTimezone = () => {
        return event?.TimeZone || event?.timeZone || 'America/New_York';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const timezone = getEventTimezone();
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: timezone
        });
    };

    const formatTime = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const timezone = getEventTimezone();
        const startTime = start.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: timezone
        });
        const endTime = end.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: timezone
        });
        return `${startTime} - ${endTime}`;
    };

    // Get timezone abbreviation (e.g., EST, EDT) for the event timezone
    const getTimezoneAbbr = () => {
        const timezone = getEventTimezone();
        const date = new Date();
        const parts = date.toLocaleString('en-US', { timeZone: timezone, timeZoneName: 'short' }).split(' ');
        return parts[parts.length - 1];
    };


    const handleBookHotel = async () => {
        // Check if we have the user's EventCompanyContact information
        if (!userEventCompanyContact) {
            setHotelBookingError('Unable to find your registration information. Please try refreshing the page.');
            return;
        }

        // Open window immediately on user click to avoid popup blocker (especially on mobile Safari)
        // We open a blank page first, then redirect after the API call completes
        const newWindow = window.open('about:blank', '_blank');
        
        try {
            setHotelBookingLoading(true);
            setHotelBookingError('');

            const response = await apiService.getHotelBookingLink(
                userEventCompanyContact.eventCompanyContactId,
                userEventCompanyContact.eventCompanyId
            );

            if (response.data.success && response.data.hotelUrl) {
                // Redirect the already-opened window to the hotel booking URL
                if (newWindow) {
                    newWindow.location.href = response.data.hotelUrl;
                } else {
                    // Fallback if window was blocked anyway
                    window.location.href = response.data.hotelUrl;
                }
            } else {
                // Close the blank window if API failed
                if (newWindow) newWindow.close();
                setHotelBookingError(response.data.message || 'Unable to generate hotel booking link');
            }
        } catch (err) {
            console.error('Error booking hotel:', err);
            // Close the blank window if there was an error
            if (newWindow) newWindow.close();
            setHotelBookingError('Failed to generate hotel booking link. Please try again.');
        } finally {
            setHotelBookingLoading(false);
        }
    };

    const handleHotelNotNeeded = async (notNeeded) => {
        if (!event?.EventId) return;

        try {
            setHotelNotNeededLoading(true);
            const response = await apiService.setHotelNotNeeded(event.EventId, notNeeded);

            if (response.data.success) {
                // Update local state to reflect the change
                setUserEventCompanyContact(prev => ({
                    ...prev,
                    hotelNotNeeded: notNeeded
                }));
            }
        } catch (err) {
            console.error('Error updating hotel preference:', err);
        } finally {
            setHotelNotNeededLoading(false);
        }
    };

    const fetchColleagues = async () => {
        if (!event?.EventId) return;

        try {
            setColleaguesLoading(true);
            const response = await apiService.getEventColleagues(event.EventId);
            if (response.data.success) {
                setColleagues(response.data.colleagues || []);
            }
        } catch (err) {
            console.error('Error fetching colleagues:', err);
            setColleagues([]);
        } finally {
            setColleaguesLoading(false);
        }
    };

    const handleOpenColleaguesDialog = async () => {
        setShowColleaguesDialog(true);
        await fetchColleagues();
    };

    const handleBookColleagueHotel = async (colleague) => {
        // Open window immediately on user click to avoid popup blocker (especially on mobile Safari)
        const newWindow = window.open('about:blank', '_blank');
        
        try {
            setColleagueBookingLoading(prev => ({ ...prev, [colleague.eventCompanyContactId]: true }));

            const response = await apiService.getColleagueHotelBookingLink(
                colleague.eventCompanyContactId,
                colleague.eventCompanyId
            );

            if (response.data.success && response.data.hotelUrl) {
                // Redirect the already-opened window to the hotel booking URL
                if (newWindow) {
                    newWindow.location.href = response.data.hotelUrl;
                } else {
                    // Fallback if window was blocked anyway
                    window.location.href = response.data.hotelUrl;
                }
                // Refresh colleagues list to update status
                await fetchColleagues();
            } else {
                // Close the blank window if API failed
                if (newWindow) newWindow.close();
                console.error('Failed to get hotel booking link:', response.data.message);
            }
        } catch (err) {
            console.error('Error booking colleague hotel:', err);
            // Close the blank window if there was an error
            if (newWindow) newWindow.close();
        } finally {
            setColleagueBookingLoading(prev => ({ ...prev, [colleague.eventCompanyContactId]: false }));
        }
    };

    const getHotelStatusBadgeClass = (status) => {
        switch (status) {
            case 'Booked':
                return 'badge-success';
            case 'Cancelled':
                return 'badge-danger';
            default:
                return 'badge-warning';
        }
    };

    // Handle opening profile preview drawer
    // Profile approval workflow (events whose entity types require admin approval before going live)
    const describeProfileReview = (entity) => {
        const base = {
            title: `Update ${entity.form_name}`,
            description: 'Complete your profile',
            status: entity.is_onboarded ? 'completed' : 'pending',
            actionText: entity.is_onboarded ? 'Edit Profile' : 'Update Profile',
            canSubmit: false,
        };
        if (!entity.requires_profile_approval) return base;
        const complete = !!entity.is_onboarded;
        switch (entity.profile_status) {
            case 'submitted':
            case 'in_review':
                return { ...base, title: entity.form_name, status: 'pending',
                    description: 'Thanks! The Funds4Charity team is reviewing your profile. You can still make edits; we\u2019ll let you know once it\u2019s live.' };
            case 'changes_requested':
                return { ...base, title: entity.form_name, status: 'pending', canSubmit: complete,
                    description: entity.profile_review_notes
                        ? `The Funds4Charity team asked for changes: \u201c${entity.profile_review_notes}\u201d Update your profile and resubmit.`
                        : 'The Funds4Charity team asked for a few changes. Update your profile and resubmit.' };
            case 'live':
                return { ...base, title: entity.form_name, status: 'completed', description: 'Your profile is live in the directory.' };
            case 'withdrawn':
                return { ...base, title: entity.form_name, status: 'pending', description: 'This profile has been withdrawn. Contact the Funds4Charity team if this is unexpected.' };
            default: // draft
                return { ...base, status: 'pending', canSubmit: complete,
                    description: complete
                        ? 'Your profile is complete. Submit it for review so the Funds4Charity team can make it live.'
                        : 'Complete your profile, then submit it for review. It goes live in the directory once approved.' };
        }
    };

    const handleSubmitForReview = async (task) => {
        if (!task.eventCompanyId) return;
        setReviewSubmitting((s) => ({ ...s, [task.eventCompanyId]: true }));
        setReviewMessage(null);
        try {
            await apiService.submitProfileForReview(task.eventCompanyId);
        } catch (err) {
            setReviewMessage({ type: 'error', eventCompanyId: task.eventCompanyId, text: err.response?.data?.detail || 'Could not submit your profile. Please try again.' });
            setReviewSubmitting((s) => ({ ...s, [task.eventCompanyId]: false }));
            return;
        }
        setReviewMessage({ type: 'success', eventCompanyId: task.eventCompanyId, text: 'Your profile has been submitted for review. We\u2019ll email you once it\u2019s live.' });
        try {
            // Refresh so the task flips to "submitted"; the submission itself already succeeded
            const entitiesResponse = await apiService.getMyCompanyEntitiesWithForms(eventNameId);
            setCompanyEntitiesWithForms(entitiesResponse.data);
        } catch (err) {
            console.warn('Profile submitted but refreshing the task list failed', err);
            // Mark the submitted entity locally so the button does not offer a second submit
            setCompanyEntitiesWithForms((prev) => (prev || []).map((e) =>
                e.event_company_id === task.eventCompanyId ? { ...e, profile_status: 'submitted' } : e));
        } finally {
            setReviewSubmitting((s) => ({ ...s, [task.eventCompanyId]: false }));
        }
    };

    const handleViewMyProfile = async (task) => {
        try {
            setProfileDrawerLoading(true);
            setProfileDrawerOpen(true);

            // Fetch the user's own company entity profile using the dedicated endpoint
            const response = await api.get(`/event-entity-companies/my/${task.companyEntityId}/profile`, { params: { event_name_id: eventNameId } });

            setProfileDrawerData({
                company: response.data.company,
                questions: response.data.questions,
                entityName: response.data.entity_name,
                entityColor: response.data.entity_color,
                // Store task IDs for the Edit Profile button navigation
                taskEntityId: task.entityId,
                taskCompanyEntityId: task.companyEntityId
            });
        } catch (err) {
            console.error('Error fetching profile preview:', err);
            setProfileDrawerData(null);
        } finally {
            setProfileDrawerLoading(false);
        }
    };

    const closeProfileDrawer = () => {
        setProfileDrawerOpen(false);
        setTimeout(() => setProfileDrawerData(null), 300);
    };

    // Helper to render drawer values (matching EventNonprofits/EventPhilanthropists style)
    const renderDrawerValue = (value, question) => {
        if (!value) {
            return <span className="drawer-empty-value">Not provided</span>;
        }

        if (typeof value === 'object' && value !== null && value.type === 'file' && value.url) {
            const isImage = value.url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
            const s3Key = value.s3_key || value.url;
            
            const handleFileClick = async (e) => {
                e.preventDefault();
                try {
                    const companyName = profileDrawerData?.company?.CompanyName || '';
                    const questionLabel = question.short_description || question.question_text || '';
                    const response = await api.post('/event-entity-companies/file-download', null, {
                        params: {
                            s3_key: s3Key,
                            company_name: companyName,
                            question_label: questionLabel
                        }
                    });
                    if (response.data?.presigned_url) {
                        window.open(response.data.presigned_url, '_blank');
                    }
                } catch (err) {
                    console.error('Error fetching file URL:', err);
                    // Fallback to direct URL if endpoint fails
                    window.open(value.url, '_blank');
                }
            };
            
            return (
                <div className="drawer-file-section">
                    {isImage && (
                        <div className="drawer-image-preview">
                            <img src={value.url} alt={question.question_text} />
                        </div>
                    )}
                    <button
                        onClick={handleFileClick}
                        className="drawer-file-link"
                    >
                        <Download size={16} />
                        View File
                    </button>
                </div>
            );
        }

        // Check field type for chip rendering
        const fieldName = (question.short_description || question.question_text || '').toLowerCase();
        const isAreasOfInterest = fieldName.includes('areas of interest') || fieldName.includes('focus area') || fieldName.includes('areas of philanthropic giving') || fieldName.includes('cause area') || fieldName.includes('primary areas');
        const isGeographicFocus = (fieldName.includes('geographic') && (fieldName.includes('footprint') || fieldName.includes('focus'))) || fieldName.includes('potential geographies');

        // Check if this is hierarchical data (like Areas of Interest) - always render as chips
        const isHierarchicalTags = typeof value === 'object' && value !== null && value.type === 'hierarchical' && Array.isArray(value.items);

        // Handle hierarchical values (like Areas of Interest) - always render as chips for consistency
        if (isHierarchicalTags) {
            // Render as chips for Areas of Interest and similar hierarchical data
            return (
                <div className="nonprofit-card-tags">
                    {value.items.map((item, idx) => (
                        <span key={idx} className="nonprofit-tag">
                            {item.category}
                            {item.subcategories && item.subcategories.length > 0 && (
                                <span className="nonprofit-tag-subs">
                                    {item.subcategories.map((sub, subIdx) => (
                                        <span key={subIdx}> [{sub}]{subIdx < item.subcategories.length - 1 ? ',' : ''}</span>
                                    ))}
                                </span>
                            )}
                        </span>
                    ))}
                </div>
            );
        }

        // Render Areas of Interest or Geographic Focus as chips when value is comma-separated string
        if ((isAreasOfInterest || isGeographicFocus) && typeof value === 'string') {
            const tags = value.split(',').map(t => t.trim()).filter(t => t);
            if (tags.length > 0) {
                return (
                    <div className="nonprofit-card-tags">
                        {tags.map((tag, idx) => (
                            <span key={idx} className="nonprofit-tag">{tag}</span>
                        ))}
                    </div>
                );
            }
        }

        const textValue = String(value);

        // Check if it's a URL
        const isUrl = textValue.match(/^(https?:\/\/|www\.)/i) ||
            (textValue.includes('.') && !textValue.includes(' ') &&
                (question.question_text?.toLowerCase().includes('website') ||
                    question.short_description?.toLowerCase().includes('website')));

        if (isUrl) {
            const href = textValue.startsWith('http') ? textValue : `https://${textValue}`;
            return (
                <div className="drawer-text-value">
                    <a href={href} target="_blank" rel="noopener noreferrer" className="drawer-link">
                        {textValue}
                    </a>
                </div>
            );
        }

        return (
            <div className="drawer-text-value">
                {textValue.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                        {line}
                        {i < textValue.split('\n').length - 1 && <br />}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const handleTaskAction = (task) => {
        if (task.id >= 100) {
            // Company entity form tasks
            navigate(`/event/${eventNameId}/entity-profile/${task.entityId}/${task.companyEntityId}`);
        } else {
            switch (task.id) {
                case 1: // Register / Continue registration
                    navigate(registerPath);
                    break;
                case 2: // Update Personal Profile / View Profile
                    if (user?.contact_id) {
                        // If profile is complete (onboarded), view the profile; otherwise, edit it
                        if (contactProfile?.is_onboarded) {
                            navigate(`/user/${user.contact_id}`);
                        } else {
                            navigate(`/user/${user.contact_id}/edit?from=home`);
                        }
                    }
                    break;
                case 3: // Book a Hotel / Manage Reservation
                    if (!task.disabled) {
                        handleBookHotel();
                    }
                    break;
                default:
                    console.log('No action for task:', task.title);
            }
        }
    };

    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <LoadingSpinner message="Loading event..." />
                    </div>
                </div>
            </>
        );
    }

    if (error || !event) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <div className="error-message">
                            <h2>Error</h2>
                            <p>{error || 'Event not found'}</p>
                            <button onClick={() => navigate('/login')} className="btn btn-primary">
                                Return to Login
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Checklist progress (display only — allTasksComplete drives the "What's next" logic)
    const visibleTasks = tasks.filter((task) => !(task.id === 3 && task.disabled));
    const donorEntitiesForProgress = companyEntitiesWithForms.filter((entity) => entity.entity_name === 'Donor' && entity.event_company_id);
    const donorDoneCount = donorEntitiesForProgress.reduce((sum, entity) => {
        const st = donorTaskStatus[entity.event_company_id];
        return sum + (st?.nominationDone ? 1 : 0) + (st?.commitmentDone ? 1 : 0);
    }, 0);
    const progressTotal = visibleTasks.length + donorEntitiesForProgress.length * 2;
    const progressDone = visibleTasks.filter((task) => task.status === 'completed').length + donorDoneCount;
    const progressPercent = progressTotal > 0 ? Math.round((progressDone / progressTotal) * 100) : 0;
    const reviewMessageInline = !!(reviewMessage?.eventCompanyId
        && tasks.some((task) => task.id >= 100 && task.eventCompanyId === reviewMessage.eventCompanyId));
    const renderReviewMessage = () => (
        <div
            className={`task-inline-message ${reviewMessage.type === 'error' ? 'is-error' : 'is-success'}`}
            role={reviewMessage.type === 'error' ? 'alert' : 'status'}
        >
            <p>{reviewMessage.text}</p>
        </div>
    );

    const archived = isEventPast(event);

    return (
        <>
            <NavigationBar />
            <div className="event-layout">
                <EventNavigation eventNameId={eventNameId} />
                <div className="event-home-container with-sidebar">
                    <div className="home-wrap">
                        {archived && (
                            <div className="home-archived-banner" role="status">
                                <Archive size={16} aria-hidden="true" />
                                <div className="home-archived-text">
                                    <strong>This event has ended.</strong> You're viewing a read-only archive of {event.Name}.
                                </div>
                                <Link to={EVENTS_LIST_PATH} className="home-archived-link">
                                    See upcoming events <ArrowRight size={14} aria-hidden="true" />
                                </Link>
                            </div>
                        )}

                        <header className="home-hero">
                            <div className="home-eyebrow">{archived ? 'Archived event' : 'Event'}</div>
                            <h1 className="home-title">{event.Name}</h1>
                            <ul className="home-meta">
                                {event.StartDate && event.EndDate && (
                                    <li>
                                        <Calendar size={15} aria-hidden="true" />
                                        <span>{formatDate(event.StartDate)} – {formatDate(event.EndDate)}</span>
                                    </li>
                                )}
                                {event.StartDate && event.EndDate && (
                                    <li>
                                        <Clock size={15} aria-hidden="true" />
                                        <span>{formatTime(event.StartDate, event.EndDate)} {getTimezoneAbbr()}</span>
                                    </li>
                                )}
                                {(event.Location || event.City) && (
                                    <li>
                                        <MapPin size={15} aria-hidden="true" />
                                        <span>
                                            {event.Location}
                                            {event.Address1 && ` · ${event.Address1}`}
                                            {event.City && `, ${event.City}`}
                                            {event.StateProvince && `, ${event.StateProvince}`}
                                        </span>
                                    </li>
                                )}
                            </ul>
                        </header>

                        <div className="event-info">
                            {showRegisterCta && (
                                <section className="home-card home-register-card" aria-labelledby="home-register-title">
                                    <div className="home-register-body">
                                        <h2 className="home-register-title" id="home-register-title">
                                            {registrationStatus === 'registering' ? 'Finish registering for this event' : 'You’re not registered for this event yet'}
                                        </h2>
                                        <p className="home-register-text">
                                            {registrationStatus === 'registering'
                                                ? 'Pick up where you left off to secure your place.'
                                                : 'Register to unlock the attendee directory, meetings, messaging and the full agenda.'}
                                        </p>
                                    </div>
                                    <Link to={registerPath} className="home-register-btn">
                                        {registrationStatus === 'registering' ? 'Continue registration' : 'Register now'} <ArrowRight size={16} aria-hidden="true" />
                                    </Link>
                                </section>
                            )}

                            {/* Your Tasks Section */}
                            <section className="home-card tasks-section" aria-labelledby="home-tasks-title">
                                <div className="tasks-header">
                                    <div>
                                        <h2 className="tasks-title" id="home-tasks-title">Your tasks</h2>
                                        <p className="tasks-subtitle">
                                            {progressTotal > 0 && progressDone === progressTotal
                                                ? 'Everything is complete. Nice work.'
                                                : 'Finish these to get the most out of the event.'}
                                        </p>
                                    </div>
                                    <span className="tasks-progress-pill" role="status">
                                        {progressDone} of {progressTotal} done
                                    </span>
                                </div>
                                <div
                                    className="tasks-progress-bar"
                                    role="progressbar"
                                    aria-label="Onboarding progress"
                                    aria-valuemin={0}
                                    aria-valuemax={progressTotal}
                                    aria-valuenow={progressDone}
                                >
                                    <div className="tasks-progress-fill" style={{ width: `${progressPercent}%` }} />
                                </div>
                                <div className="tasks-list">
                                    {tasks.map((task) => {
                                        const isComplete = task.status === 'completed';
                                        const stepNumber = visibleTasks.indexOf(task) + 1;
                                        const isEntityTask = task.id >= 100;
                                        const submitIsPrimary = isEntityTask && task.canSubmitForReview;
                                        const showStatusPill = isEntityTask && task.requiresApproval && task.profileStatus && PROFILE_STATUS_LABEL[task.profileStatus];
                                        const showInlineMessage = reviewMessageInline && isEntityTask && task.eventCompanyId === reviewMessage.eventCompanyId;
                                        return (
                                            <React.Fragment key={task.id}>
                                                <div className={`task-card ${isComplete ? 'is-complete' : ''}`}>
                                                    <div className="task-status">
                                                        {isComplete ? (
                                                            <CheckCircle size={20} className="task-checkmark" aria-label="Done" />
                                                        ) : stepNumber > 0 ? (
                                                            <span className="task-circle task-step" aria-label={`Step ${stepNumber}`}>{stepNumber}</span>
                                                        ) : (
                                                            <Circle size={20} className="task-circle" />
                                                        )}
                                                    </div>

                                                    <div className="task-content">
                                                        <div className="task-heading">
                                                            <h3 className="task-title">{task.title}</h3>
                                                            {showStatusPill && (
                                                                <span className={`status-pill status-${task.profileStatus}`}>
                                                                    {PROFILE_STATUS_LABEL[task.profileStatus]}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="task-description">{task.description}</p>

                                                        {/* Hotel not needed checkbox - only show on hotel task */}
                                                        {task.id === 3 && (
                                                            <div className="hotel-not-needed-row">
                                                                <label className="hotel-not-needed-label">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={task.hotelNotNeeded || false}
                                                                        onChange={(e) => handleHotelNotNeeded(e.target.checked)}
                                                                        disabled={hotelNotNeededLoading}
                                                                        className="hotel-not-needed-checkbox"
                                                                    />
                                                                    <span>I do not require hotel accommodations</span>
                                                                    {hotelNotNeededLoading && <span className="loading-spinner-small" />}
                                                                </label>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="task-actions-row">
                                                        {/* View My Profile - ghost; only on entity tasks when onboarded */}
                                                        {isEntityTask && task.isOnboarded && (
                                                            <button
                                                                className="task-action-btn view-profile-btn ghost"
                                                                onClick={() => handleViewMyProfile(task)}
                                                            >
                                                                <Eye size={16} />
                                                                View my profile
                                                            </button>
                                                        )}

                                                        {/* Event Colleagues - secondary; only on hotel task if there are confirmed colleagues and user has a reservation */}
                                                        {task.id === 3 && hasConfirmedColleagues && (userEventCompanyContact?.passKeyEventStatusId === 0 || userEventCompanyContact?.passKeyEventStatusId === 2) && (
                                                            <button
                                                                className="task-action-btn colleagues-btn secondary"
                                                                onClick={handleOpenColleaguesDialog}
                                                            >
                                                                <Users size={16} />
                                                                Event Colleagues
                                                            </button>
                                                        )}

                                                        {/* Main action - primary unless "Submit for review" takes that role */}
                                                        {task.actionText && (
                                                            <button
                                                                className={`task-action-btn ${submitIsPrimary ? 'secondary' : ''} ${task.disabled ? 'disabled' : ''}`}
                                                                onClick={() => handleTaskAction(task)}
                                                                disabled={
                                                                    task.disabled ||
                                                                    (task.id === 3 && hotelBookingLoading) ||
                                                                    (task.id === 3 && !userEventCompanyContact)
                                                                }
                                                            >
                                                                {task.id === 3 && hotelBookingLoading ? 'Generating...' :
                                                                    task.actionText}
                                                            </button>
                                                        )}

                                                        {/* Submit for review - primary on approval-gated entity tasks */}
                                                        {submitIsPrimary && (
                                                            <button
                                                                className="task-action-btn submit-review-btn"
                                                                onClick={() => handleSubmitForReview(task)}
                                                                disabled={!!reviewSubmitting[task.eventCompanyId]}
                                                            >
                                                                <Send size={16} />
                                                                {reviewSubmitting[task.eventCompanyId] ? 'Submitting…' : (task.profileStatus === 'changes_requested' ? 'Resubmit for review' : 'Submit for review')}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                {showInlineMessage && renderReviewMessage()}
                                            </React.Fragment>
                                        );
                                    })}

                                    {/* Donor Circle tasks (Donor organizations only) */}
                                    {companyEntitiesWithForms
                                        .filter((entity) => entity.entity_name === 'Donor' && entity.event_company_id)
                                        .map((entity, donorIndex) => (
                                            <DonorCircleTasks
                                                key={`donor-${entity.event_company_id}`}
                                                eventCompanyId={entity.event_company_id}
                                                stepStart={visibleTasks.length + donorIndex * 2 + 1}
                                                onStatusChange={(status) => handleDonorTaskStatus(entity.event_company_id, status)}
                                            />
                                        ))}
                                </div>

                                {reviewMessage && !reviewMessageInline && renderReviewMessage()}

                                {hotelBookingError && (
                                    <div className="task-inline-message is-error" role="alert">
                                        <p>{hotelBookingError}</p>
                                    </div>
                                )}
                            </section>

                            {/* What's Next Section */}
                            {allTasksComplete && (
                                <section className="home-card whats-next-section" aria-labelledby="home-whats-next-title">
                                    <h2 className="whats-next-title" id="home-whats-next-title">What's next</h2>
                                    <div className="whats-next-content">

                                        <ul className="whats-next-bullets">
                                            <li>
                                                Thank you! Your profile is complete. We're thrilled to have your organization join us at <a href="https://iconnections.io/funds4charity/" target="_blank" rel="noopener noreferrer" className="whats-next-link">Funds 4 Charity</a>, where nonprofits and philanthropic investors connect to accelerate measurable impact.
                                            </li>
                                            <li>
                                                The scheduling app will open in January. You will receive an email with clear instructions and a timeline for how to engage.
                                            </li>
                                        </ul>
                                        {isNonProfit && (
                                            <p>
                                                Please review the <a href="https://iconnections.io/wp-content/uploads/2025/11/Participation-Guide-.pdf" target="_blank" rel="noopener noreferrer" className="guide-link">Non-Profit Participation Guide</a> to revisit best practices for donor meetings and begin preparing for your upcoming conversations with philanthropists.
                                            </p>
                                        )}
                                    </div>
                                </section>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Event Colleagues Dialog */}
            {showColleaguesDialog && (
                <div className="colleagues-dialog-overlay" onClick={() => setShowColleaguesDialog(false)}>
                    <div className="colleagues-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="colleagues-dialog-header">
                            <h2>
                                <Users size={24} />
                                Event Colleagues
                            </h2>
                            <button
                                className="colleagues-dialog-close"
                                onClick={() => setShowColleaguesDialog(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="colleagues-dialog-content">
                            {colleaguesLoading ? (
                                <div className="colleagues-loading">
                                    <LoadingSpinner message="Loading colleagues..." />
                                </div>
                            ) : colleagues.length === 0 ? (
                                <div className="colleagues-empty">
                                    <Users size={48} className="colleagues-empty-icon" />
                                    <p>No confirmed colleagues found from your company.</p>
                                    <p className="colleagues-empty-hint">
                                        Colleagues must have a confirmed event status to appear here.
                                    </p>
                                </div>
                            ) : (
                                <div className="colleagues-list">
                                    {colleagues.map((colleague) => (
                                        <div key={colleague.eventCompanyContactId} className="colleague-card">
                                            <div className="colleague-info">
                                                <div className="colleague-name">
                                                    {colleague.firstName} {colleague.lastName}
                                                </div>
                                                <div className="colleague-email">
                                                    {colleague.email}
                                                </div>
                                            </div>
                                            <div className="colleague-hotel-status">
                                                <span className={`hotel-status-badge ${getHotelStatusBadgeClass(colleague.hotelStatus)}`}>
                                                    <Hotel size={14} />
                                                    {colleague.hotelStatus}
                                                </span>
                                            </div>
                                            <div className="colleague-actions">
                                                {colleague.canBookHotel && colleague.hotelStatus === 'Not Booked' && (
                                                    <button
                                                        className="colleague-book-btn"
                                                        onClick={() => handleBookColleagueHotel(colleague)}
                                                        disabled={colleagueBookingLoading[colleague.eventCompanyContactId]}
                                                    >
                                                        {colleagueBookingLoading[colleague.eventCompanyContactId]
                                                            ? 'Booking...'
                                                            : 'Book Hotel'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Profile Preview Drawer */}
            {profileDrawerOpen && (
                <>
                    <div className="drawer-overlay" onClick={closeProfileDrawer} />
                    <div className={`profile-drawer ${profileDrawerOpen ? 'open' : ''}`}>
                        <div className="drawer-header">
                            <h2>{profileDrawerData?.company?.CompanyName || 'Loading...'}</h2>
                            <button className="drawer-close-btn" onClick={closeProfileDrawer} aria-label="Close drawer">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="drawer-content">
                            {profileDrawerLoading ? (
                                <div className="drawer-loading">
                                    <LoadingSpinner message="Loading profile preview..." />
                                </div>
                            ) : !profileDrawerData?.company ? (
                                <div className="drawer-empty">
                                    <p>Could not load profile preview.</p>
                                </div>
                            ) : (
                                <>
                                    {/* Entity Tag */}
                                    {profileDrawerData.entityName && (
                                        <span
                                            className="drawer-entity-tag"
                                            style={profileDrawerData.entityColor ? {
                                                '--entity-bg-color': `${profileDrawerData.entityColor}20`,
                                                '--entity-border-color': `${profileDrawerData.entityColor}40`,
                                                '--entity-text-color': profileDrawerData.entityColor
                                            } : undefined}
                                        >
                                            {profileDrawerData.entityName}
                                        </span>
                                    )}

                                    {/* Organization Contacts */}
                                    {profileDrawerData.company.contacts && profileDrawerData.company.contacts.length > 0 && (
                                        <div className="drawer-contacts-section">
                                            {profileDrawerData.company.contacts.map((contact) => (
                                                <div
                                                    key={contact.ContactId}
                                                    className="drawer-contact-item"
                                                    onClick={() => window.open(`/user/${contact.ContactId}`, '_blank')}
                                                    title="View profile"
                                                >
                                                    {contact.ImageUrl ? (
                                                        <img
                                                            src={contact.ImageUrl}
                                                            alt={`${contact.FirstName} ${contact.LastName}`}
                                                            className="drawer-contact-avatar"
                                                        />
                                                    ) : (
                                                        <div className="drawer-contact-avatar-placeholder">
                                                            {contact.FirstName?.[0]}{contact.LastName?.[0]}
                                                        </div>
                                                    )}
                                                    <div className="drawer-contact-info">
                                                        <div className="drawer-contact-name">
                                                            {contact.FirstName} {contact.LastName}
                                                        </div>
                                                        {contact.JobTitle && (
                                                            <div className="drawer-contact-title">
                                                                {contact.JobTitle}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Profile Fields */}
                                    {profileDrawerData.questions
                                        ?.filter((question) => {
                                            const value = profileDrawerData.company.answers?.[question.question_id];
                                            if (value === null || value === undefined || value === '') {
                                                return false;
                                            }
                                            // Hide specific financial questions for philanthropists
                                            const questionText = (question.short_description || question.question_text || '').toLowerCase();
                                            if (questionText.includes('average annual philanthropic giving') && questionText.includes('last three years')) {
                                                return false;
                                            }
                                            if (questionText.includes('philanthropic giving projected to grow')) {
                                                return false;
                                            }
                                            if (questionText.includes('projected capacity to give to new charitable organizations')) {
                                                return false;
                                            }
                                            return true;
                                        })
                                        .sort((a, b) => {
                                            // Custom sort order to match nonprofit/philanthropist drawers
                                            const getOrder = (q) => {
                                                const text = (q.short_description || q.question_text || '').toLowerCase();
                                                // Philanthropy Type first (philanthropist specific)
                                                if (text.includes('philanthropy type') || text.includes('type of philanthropy') || text.includes('donor type')) return 1;
                                                // Description/Mission second
                                                if (text.includes('organization description') || text.includes('org description') || text.includes('mission')) return 2;
                                                // Areas of Interest/Giving third
                                                if (text.includes('areas of interest') || text.includes('focus area') || text.includes('areas of philanthropic giving')) return 3;
                                                // Geographic Focus fourth
                                                if (text.includes('geographic') && (text.includes('footprint') || text.includes('focus'))) return 4;
                                                // Potential Geographies fifth (philanthropist specific)
                                                if (text.includes('potential geographies')) return 5;
                                                // Current approach sixth (philanthropist specific)
                                                if (text.includes('current approach')) return 6;
                                                return 100; // All other fields come after
                                            };
                                            return getOrder(a) - getOrder(b);
                                        })
                                        .map((question) => {
                                            const value = profileDrawerData.company.answers?.[question.question_id];
                                            let labelText = question.short_description || question.question_text;
                                            
                                            // Transform labels for better display (matching nonprofit/philanthropist pages)
                                            if (labelText?.toLowerCase().includes('donor areas of interest')) {
                                                labelText = labelText.replace(/donor\s+/i, '');
                                            }
                                            if (labelText?.toLowerCase().includes('geographic footprint')) {
                                                labelText = labelText.replace(/footprint/i, 'Focus');
                                            }
                                            if (labelText?.toLowerCase().includes('primary areas of philanthropic giving')) {
                                                labelText = 'Areas of Philanthropic Giving';
                                            }
                                            // Question 85 - Geographic Focus (philanthropist)
                                            if (question.question_id === 85) {
                                                labelText = 'Geographic Focus';
                                            }
                                            // Question 53 - Giving Regions (philanthropist)
                                            if (question.question_id === 53) {
                                                labelText = 'Giving Regions';
                                            }
                                            // Question 84 - Geographic Focus (non-profit)
                                            if (question.question_id === 84) {
                                                labelText = 'Geographic Focus';
                                            }
                                            // Question 67 - Service Locations (non-profit)
                                            if (question.question_id === 67) {
                                                labelText = 'Service Locations';
                                            }
                                            if (labelText?.toLowerCase().includes('potential geographies for future giving')) {
                                                labelText = 'Potential Regions For Future Giving';
                                            }
                                            if (labelText?.toLowerCase().includes('current approach to philanthropic giving')) {
                                                labelText = 'Current Approach To Philanthropic Giving';
                                            }
                                            // Financials - truncate long description
                                            if (labelText?.toLowerCase().startsWith('financials')) {
                                                labelText = 'Financials';
                                            }
                                            // Generic cleanup
                                            if (labelText) {
                                                labelText = labelText.replace(/\s*\(check all that apply\)/gi, '');
                                                labelText = labelText.replace(/\s*\(select as many as apply\)/gi, '');
                                            }

                                            return (
                                                <div key={question.question_id} className="drawer-field">
                                                    <label className="drawer-field-label">
                                                        {labelText}
                                                    </label>
                                                    <div className="drawer-field-value">
                                                        {renderDrawerValue(value, question)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </>
                            )}
                        </div>

                        <div className="drawer-footer">
                            <button
                                className="drawer-edit-btn"
                                onClick={() => {
                                    closeProfileDrawer();
                                    // Use the stored task IDs from when the drawer was opened
                                    if (profileDrawerData?.taskEntityId && profileDrawerData?.taskCompanyEntityId) {
                                        navigate(`/event/${eventNameId}/entity-profile/${profileDrawerData.taskEntityId}/${profileDrawerData.taskCompanyEntityId}`);
                                    }
                                }}
                            >
                                Edit Profile
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default EventHome;

